import { LoadoutCondition } from '../edit-transaction/loadout-condition'
import { ColumnDescriptor, RpsSource } from './field-catalog.model'

/**
 * The metadata layer behind the Create/Edit Transaction modal (PVAL-26341).
 *
 * The modal builds itself from this description rather than hardcoding per-field behaviour, so
 * curating a new trade type is a config change. In Phase 1 the config is engineer-authored and
 * checked in (see `registry/catalogs/transaction-metadata.config.json`); it is reached only through
 * {@link TransactionMetadataSource}, so swapping the static config for a real metadata endpoint
 * later does not reshape any consumer.
 *
 * The common core — field key, semantic type, display label — is deliberately *composed* from
 * {@link ColumnDescriptor}, the grid's field-catalog API mirror, rather than restated here. That
 * file is a verbatim copy of a backend contract (PVAL-26065 / helios!434) and must not be edited to
 * carry modal-only concerns; those live in {@link TransactionFieldMetadata} instead.
 */

/**
 * The subset of the grid's catalog contract the modal shares with it: `field` is the sole join key,
 * `label` is the display name, `type` is the semantic data type.
 */
export type DataType =
  | 'string'
  | 'integer'
  | 'number'
  | 'boolean'
  | 'date'
  | 'array'
  | 'object'

export interface TransactionFieldCore
  extends Pick<ColumnDescriptor, 'field' | 'label'> {
  /**
   * The design's own Data Type — the JSON type Enfusion's docs declare.
   *
   * Deliberately NOT the grid catalog's `ColumnType` (TEXT / NUM / DATE / BOOL). The two look
   * interchangeable and are not: the grid's is a rendering hint for a report column, this is the
   * shape of the value on the write API — `integer` vs `number` matters to a payload and means
   * nothing to a column. Sharing `field` and `label` with the catalog is exact; sharing `type`
   * would have been a coincidence of spelling — which is why this is `dataType` and
   * {@link ColumnDescriptor}'s stays `type`.
   */
  dataType: DataType
}

/**
 * Which dialog context(s) a field is valid in — the metadata layer's Key Group column, token for
 * token. `common` is the trade core every trade schema composes; the rest name one Enfusion schema
 * each. GL Entry's group is `gl`, not `glEntry`: the tran category key and the key group are different
 * things and the table spells this one `gl`.
 *
 * A group nobody declares simply does not exist here any more. `trs` and `fx` used to be carried as
 * dormant exceptions, because the trade-type table named them while Enfusion published no schema of
 * its own for either. That table is gone — the Edit path resolves on Instrument Type against the
 * document — so an unconfigured value now blocks naming itself rather than routing to a group with
 * no fields behind it.
 *
 * **Deliberately `string`, not a closed union.** The set lives in the metadata document, so
 * enumerating it here would mean a helios-ui release every time a group is added to the table —
 * exactly what the metadata layer exists to prevent. The alias stays because it documents intent at
 * the call sites; it just no longer claims to know the members.
 */
export type KeyGroup = string

/**
 * How a field's allowed values are sourced. `enum` fetches the whole list up front, `lookup` is a
 * typeahead search. ~15 Enfusion reference endpoints are unconfirmed, so a `lookup` field with no
 * resolvable list degrades to free-text entry rather than blocking the user.
 */
export type ListType = 'none' | 'enum' | 'lookup'

/**
 * One selectable value of a list-backed field.
 *
 * A PAIR, never a bare string: `value` is what the form submits and what a save payload carries,
 * `label` is what the picker displays. The document authors the two identically on every option it
 * carries today, so nothing visibly depends on them differing yet — but the pair is what makes an
 * id-backed list (submit `585179`, show `GOLDMAN SACHS & CO`) authorable without touching this code
 * again, and it is the reason both templates bind `option.value` rather than the option itself.
 */
// A type alias rather than an interface so an option is assignable to {@link ListRecord}: a static
// option is one more record the PVAL-26685 normalizer reads by key.
export type Option = {
  /** Submitted on save. */
  value: string
  /** Displayed in the picker. */
  label: string
  /**
   * A disambiguating second line — a counterparty's legal name under its short name.
   *
   * Declared because the served contract carries it; **authored nowhere** in the Phase 1 document (0
   * occurrences) and rendered by nothing. PVAL-26493 deliberately left it unbuilt: the reporter's
   * answer this run was "no secondary value as of now", and the example that justified it
   * (`counterpartyId`) is a `catalog` list whose options this phase does not resolve at all.
   */
  secondary?: string
}

/**
 * A field's value list, served WITH the field rather than fetched by id — the document's own
 * `listValues` block, discriminated on `mode`.
 *
 *  - `static`  — the whole closed set is authored in the document. The 38 fields that carry this are
 *                the ones the dialog can render as a populated picker with no network call at all.
 *  - `catalog` — a reference catalogue held behind `POST dialog-metadata/resolve`. The document
 *                serves it WITHOUT its options and without the upstream that holds them — RPS's
 *                own served model has no property a URL could occupy, deliberately — so it
 *                arrives carrying `status` ('unavailable'). {@link HttpDialogListValuesService}
 *                fills `options` in once the resolve call answers, after which the field is
 *                indistinguishable from a `static` one. That is the serving tier's own reading:
 *                its `ServedListValues.Options` "covers `static` and resolved `catalog` alike,
 *                indistinguishably" (PVAL-26442 / PVAL-26563).
 *  - `search`  — a typeahead against that endpoint, after `minChars` characters.
 *
 * A list with no options renders a DISABLED control — see `EditTransactionField.listPending` —
 * which is deliberately distinguishable from a field that declares a list type and carries no
 * `listValues` at all, and which stays free text.
 */
export type TransactionListValues =
  | ({ mode: 'static'; options: Option[] } & Partial<ListDisplayKeys>)
  | ({
      mode: 'catalog'
      status?: string
      /** The resolved ROWS, as `/resolve` answered them — read through {@link ListDisplayKeys}. */
      options?: ListRecord[]
    } & ListDisplayKeys &
      ListRequestKeys)
  | ({ mode: 'search'; minChars: number } & ListDisplayKeys & ListRequestKeys)

/** One upstream row (or one static option) — read only by the keys {@link ListDisplayKeys} names. */
export type ListRecord = Readonly<Record<string, unknown>>

/**
 * The response keys RPS serves on every lookup (PVAL-26685), which say how a row reads:
 *
 *  - `displayColumns` — the dotted names of the columns the picker shows, in order;
 *  - `displayText`    — the column the closed control shows once a row is picked;
 *  - `valueKey`       — the column written to the field (and submitted).
 *
 * Optional on a `static` list, where RPS defaults them (`label` / `label` / `value`) and so does
 * {@link listDisplayKeys}; required on `catalog` and `search`, whose rows have no other way to say
 * which key is the id. An un-keyed lookup (one served by an RPS that predates its PVAL-26685 half)
 * arrives without them and takes the static defaults in the adapter, so it yields no rows until
 * RPS keys it.
 */
export interface ListDisplayKeys {
  displayColumns: string[]
  displayText: string
  valueKey: string
}

/**
 * The request keys RPS serves on a lookup (PVAL-26685): the names of the `params` keys its
 * `/resolve` call takes — the stored `request.getRequest`, then each whole-value
 * `request.postRequest` token. Names only; the url, the body and every value stay on RPS.
 *
 * RPS defines none of the names. The stored url and body decide them, so the dialog sends the ones
 * it holds a value for and leaves the rest out (see `HttpDialogListValuesService`). Absent on a
 * lookup whose stored spec takes no params, and on every `static` list.
 */
export interface ListRequestKeys {
  requestKeys?: string[]
}

/**
 * A list's response keys, each defaulted independently the way RPS's served static list is:
 * `["label"]` (plus `"secondary"` when any option carries one), `"label"`, `"value"`.
 *
 * `null` for a field with no list at all.
 */
export const listDisplayKeys = (
  listValues?: TransactionListValues | null,
): ListDisplayKeys | null => {
  if (listValues == null) return null
  const authored = listValues as Partial<ListDisplayKeys>
  const options =
    'options' in listValues && Array.isArray(listValues.options)
      ? (listValues.options as ListRecord[])
      : []
  const defaultColumns = options.some(option => option.secondary != null)
    ? ['label', 'secondary']
    : ['label']
  const displayColumns =
    Array.isArray(authored.displayColumns) && authored.displayColumns.length
      ? authored.displayColumns
      : defaultColumns
  const displayText = authored.displayText || 'label'
  // A row's id defaults to the column it is shown by (RPS's own rule for catalog / search); a static
  // option's to its `value`, which is what an authored option submits.
  const valueKey =
    authored.valueKey ||
    (listValues.mode !== 'static' && authored.displayText
      ? displayText
      : 'value')
  return { displayColumns, displayText, valueKey }
}

/**
 * The records a list carries, whatever mode put them there — the ONE place "does this field have a
 * list yet" is decided.
 *
 * It lives beside the type rather than in the projection because both the projection
 * (`listOptionsOf`) and the metadata source (`unresolvedListKeys`) have to answer it, and the
 * projection already imports this file while the reverse would be a cycle.
 *
 * `?? null` rather than a truthiness test: an authored-but-empty `static` list still answers `[]`,
 * because "a closed set with nothing in it" is a different fact from "no list yet".
 */
export const listValueOptions = (
  listValues?: TransactionListValues | null,
): readonly ListRecord[] | null =>
  listValues == null
    ? null
    : ('options' in listValues ? listValues.options : undefined) ?? null

/**
 * How many characters a `search` list wants before it is worth asking — the document's own
 * `minChars`. `null` for every other mode, which is the test for "is this field searchable at
 * all": a `lookup` field with no `listValues` names no upstream and is ordinary free text.
 */
export const listValueMinChars = (
  listValues?: TransactionListValues | null,
): number | null => (listValues?.mode === 'search' ? listValues.minChars : null)

/**
 * The `params` keys a list's `/resolve` call takes, as served. Empty for a `static` list, and for a
 * lookup whose stored spec takes none.
 */
export const listRequestKeys = (
  listValues?: TransactionListValues | null,
): readonly string[] =>
  (listValues != null && 'requestKeys' in listValues
    ? listValues.requestKeys
    : undefined) ?? []

/**
 * Where a field sits structurally — the *structural half* of the design's Display Container column.
 *
 *  - `default` — an ordinary row of the dialog. What a field is unless it says otherwise.
 *  - `arraySummary` — the one row standing for a whole collection.
 *  - `arrayField` — a column of that collection, edited in its items modal.
 *
 * Three tokens, not five: the design's column has five values, and two of them are presentations of
 * a structure rather than structures of their own. {@link DisplayPresent} carries those two, and the
 * correspondence table lives on it — read the two together, never one alone.
 */
export type DisplayContainer = 'default' | 'arraySummary' | 'arrayField'

/**
 * A presentation override layered on top of {@link DisplayContainer} — the design's `present`.
 *
 * Structure and presentation are separate in the metadata: Notes is structurally a `default` row
 * that is *presented* in the footer, and a GL distribution line's amount is structurally an
 * `arrayField` that is *presented* as a +|- split (its display name, `Debit|Credit`, names both
 * halves). Folding these into the container would make a field have to choose between saying where
 * it lives and saying how it is drawn.
 *
 *  - `footerField` — lifted out of the row list into a full-width field in the footer.
 *  - `signedSplit` — one signed number drawn as two mutually exclusive cells.
 *
 * ## The five-value correspondence
 *
 * The design names FIVE Display Containers; this metadata encodes them as a
 * `displayContainer` × `present` pair. All five, exhaustively:
 *
 * | design's Display Container | `displayContainer` | `present`      |
 * | -------------------------- | ------------------ | -------------- |
 * | Default                    | `default`          | *(absent)*     |
 * | Footer                     | `default`          | `footerField`  |
 * | Array Summary              | `arraySummary`     | *(absent)*     |
 * | Array                      | `arrayField`       | *(absent)*     |
 * | ±Split                     | `arrayField`       | `signedSplit`  |
 *
 * Those five pairs are the whole vocabulary — no other combination is meaningful, and the config
 * spec asserts the document uses no other. Two consequences worth stating out loud, because reading
 * either type on its own gets them wrong:
 *
 *  - a five-value design column does NOT mean a five-value TypeScript union, so `DisplayContainer`
 *    having three members is not a curation gap;
 *  - `Footer` and `±Split` are the only design values that need `present` at all, which is why
 *    `present` is optional and absent on most fields.
 *
 * Note the current Phase 1 curation exercises all five: `notes` is authored as `Footer` (`default` ×
 * `footerField`), which is where the design puts it — a memo is the one field a grid cell cannot
 * hold. Curating it there is a one-key config change and no code change, which is the property this
 * pair exists to have.
 *
 * ## Why this is still spelled `present` and not `presentAs`
 *
 * `presentAs` would read better as a property name — this is a noun-shaped key holding what reads
 * like an adverbial value. It stays `present` anyway, and the reason is coordination, not taste:
 * PVAL-26382's open Q6 asks whether `transaction_field_metadata` should also persist `label`,
 * `present` and `collection`, naming this property by THIS spelling. Renaming mid-flight would
 * orphan an in-flight blocking question against a document of record that tier owns. The spelling
 * is theirs to pick first; if they pick `presentAs`, this follows them.
 */
export type DisplayPresent = 'footerField' | 'signedSplit'

/**
 * Cell formatting for a value — the design's Display Style column. Purely presentational, never a
 * semantic type.
 *
 *  - `default` — as the source sent it. What a field is unless it says otherwise.
 *  - `prettyNumber` — thousands separators and at least two decimals.
 *  - `dateUS` — MM/DD/YYYY whatever the source sent.
 */
export type DisplayStyle = 'default' | 'prettyNumber' | 'dateUS'

/**
 * How several matched/array rows collapse to one displayed value — the design's Grouping Logic.
 *  - `same` — every line agrees, so the first answer stands for all of them. The default.
 *  - `count` — how many lines there are, not what they hold.
 *  - `sum` — add every line.
 *  - `condSum` — add only the lines whose condition field is one of the configured values (GL
 *    distribution lines: sum amount only where the account subtype is a cash subtype).
 */
export type GroupingLogic = 'same' | 'count' | 'sum' | 'condSum'

export interface GroupingRule {
  logic: GroupingLogic
  /**
   * Sub-field the condition reads. Required for `condSum`.
   *
   * A line-level key, so either spelling resolves: the bare leaf (`accountSubType`) or the collection
   * path (`distributionLines.accountSubType`). The projection takes the leaf of whatever is written,
   * because a line object is keyed by the leaf alone.
   *
   * On an `arraySummary` this may be left absent to inherit the adding column's condition — see the
   * ownership note on {@link FieldSettings.grouping}.
   */
  conditionField?: string
  /** Values the condition field must be one of. Required for `condSum`. */
  conditionValues?: string[]
}

/**
 * Datapoint keys on the matched counterpart shown alongside this field for comparison. Custody and
 * Trading are separate Prometheus feeds and do not always agree on a key name. `clearwaterKey` is
 * carried for completeness and is not read in Phase 1.
 *
 * ## What a key is allowed to be
 *
 * A key names a datapoint ON THAT SOURCE'S ROW, and a row carries datapoints in two places: modeled
 * catalog fields in `WorkbenchRow.fields`, and Prometheus's unmodeled AggregateInformation blob in
 * `WorkbenchRow.aggregateInfo`. Either is a legal key here; the projection resolves the modeled map
 * first and the blob second.
 *
 * A blob key is spelled VERBATIM — `Trade_Date`, never `aggregateInfo.Trade_Date`. There is no
 * dotted path form: `AggregateInfoService` publishes one dynamic column per blob key with `field`
 * set to the key unprefixed, because the catalog contract is that `field` is the only join key, and
 * a prefixed spelling here matches nothing anywhere. It was tried, and every comparison cell in the
 * dialog silently rendered blank.
 *
 * UNVERIFIED (PVAL-26341): whether `Trade_Date`, `Notional_Quantity` and their siblings are the real
 * Prometheus blob spellings cannot be confirmed without live data — the mock blob shares none of
 * them, so a curation typo here would look exactly like an absent counterpart value. The resolution
 * mechanism is now correct; the spellings still need one look at a real Custody/Trading row.
 */
export interface ComparisonMapping {
  custodyKey?: string
  tradingKey?: string
  clearwaterKey?: string
}

/**
 * Where the `{tokens}` in a field's Derivation Parameters resolve from — the design's Derived From.
 *
 *  - `field`   — re-derived whenever a field named in the parameters changes. The derivation wins:
 *                a value already in the cell is overwritten.
 *  - `context` — resolved once when the modal opens, from the session it was opened in. No field
 *                triggers it.
 *
 * A field with neither is not derived. Adding a computed mode later is one more member here, not a
 * second metadata column.
 */
export type DerivedFrom = 'field' | 'context'

/**
 * One input of a derivation, as RPS serves it (`DerivationInput`) — the binding with its token name
 * stripped and NO url, because the browser must never receive an upstream one (PVAL-26685).
 *
 *  - `from: 'context'` reads `key` off the session the dialog was opened in (`accountId`);
 *  - `from: 'field'` reads the sibling `field`. `scope: 'row'` means the sibling on the SAME
 *    collection line, which is how a distribution line's sub-type follows that line's account.
 *
 * The `/resolve` item's `params` is keyed by `field ?? key`, which is what RPS binds back onto the
 * stored spec's tokens.
 */
export interface DerivationInput {
  from: DerivedFrom
  key?: string
  field?: string
  scope?: 'row'
}

/**
 * Save-side field rules.
 *
 * Both Req halves are ENFORCED in Phase 1, because both are answerable without a backend, and the
 * design's Field Settings text defines the column as "Req / Req Parameter — required to save":
 *
 *  - bare {@link required} asks whether the field was answered AT ALL. Emptiness is entirely
 *    client-side, so a blank required field is counted in the dialog's missing-required note and
 *    blocks Save.
 *  - {@link requiredParameter} asks whether the value that IS there carries enough words — also a
 *    purely local judgement. A field whose value breaks its own rendered rule blocks Save too, so
 *    the hint, the footer count and the button cannot contradict.
 *
 * Neither half claims a supplied value is *valid*. Whether the instrument is legal, the account
 * exists or the date falls inside the period is a save-side question, and that endpoint lands with
 * the edit-transaction backend framework; nothing here stands in for it.
 */
export interface FieldSettings {
  /** Visible in the modal at all. */
  shown: boolean
  /** Editable vs. read-only. */
  edit: boolean
  /**
   * Required to save — and enforced as such: a field carrying this flag with nothing in it blocks
   * Save. Emptiness is the one thing about a value that needs no backend to judge, and
   * the design's Field Settings text defines the setting as "required to save". Whether a value that
   * IS there is *legal* is the save endpoint's question and is still open.
   */
  required?: boolean
  /**
   * Parameterises {@link required}: the minimum number of words the value must carry.
   *
   * Only meaningful alongside `required` — the parameter parameterises that flag rather than
   * standing on its own, and the config spec rejects one authored without it. The checked-in config
   * curates it on `notes` (the design's own worked example: a Notes field that has to say
   * something, not just be non-empty). Evaluated client-side by
   * `edit-transaction.projection.ts`'s `requiredRuleUnmet`; an UNTOUCHED BLANK is not a *rule*
   * violation (the bare {@link required} flag already refuses it, and counting it twice would double
   * it in the footer note), so the parameter only ever judges a value someone supplied.
   */
  requiredParameter?: number
  /**
   * How this field collapses several lines to one value.
   *
   * An ordinary field owns both halves of that: its own logic, over its own key. An `arraySummary`
   * owns only the AGGREGATION — the target comes from its collection's *adding* columns (the ones
   * whose own logic is `sum` / `condSum`), because a line carries `amount`, never the collection's
   * own name. So the columns say what there is to add and the summary says how:
   *
   *  - `count` needs no target at all, and is the only truthful rule for a collection whose columns
   *    merely agree (`keywords`, `nearLeg`, `farLeg`);
   *  - `sum` / `condSum` total each adding column's key. A `condSum` summary that names no
   *    `conditionField` inherits the adding column's condition, so authoring the condition on the
   *    summary does not have to retire the column's copy — the column's still drives the Σ row in the
   *    items modal. When both name one, the summary's wins, and the config spec asserts they agree.
   *
   * Absent on an `arraySummary` means "derive it from the columns' own rules" — the pre-existing
   * behaviour, kept so a summary saved through PVAL-26382's CRUD API with no logic still reads as
   * something. The checked-in config authors one on every summary.
   */
  grouping?: GroupingRule
}

/** One field's full metadata: the shared core plus the modal-only concerns. */
export interface TransactionFieldMetadata extends TransactionFieldCore {
  /** Origin system for the datapoint. Not hardcoded to Enfusion — the modal is source-agnostic. */
  source: RpsSource
  keyGroups: KeyGroup[]
  /**
   * Field order within the modal.
   *
   * Fractional for a collection's columns — `otherPayments` sits at 46 and its columns at 46.1 and
   * 46.2 — so a column sorts under its own summary row. The `.1` / `.4` / `.7` gaps the metadata
   * layer leaves are insertion room, so a new field can be curated between two existing ones without
   * renumbering the document.
   *
   * MUST be distinct across the whole config. `order` is the only thing that orders the dialog, and
   * the sort is not stable-by-anything-else, so a reused value silently reshuffles a section between
   * runs. `transaction-metadata.config.spec.ts` enforces this — it asserts every `order` is finite
   * and that the set of them is the same size as the list — so a duplicate fails a test rather than
   * reaching a user. Use the insertion room; do not reuse a number.
   */
  order: number
  /** Section heading the field is grouped under. */
  displayGroup: string
  displayContainer: DisplayContainer
  /** Presentation override on top of the container. Absent for most fields. */
  present?: DisplayPresent
  displayStyle: DisplayStyle
  /**
   * Named collection this field reads. For an `arraySummary` / `array` field it is the collection the
   * field belongs to; for any field with a {@link GroupingRule} it is the collection the rule
   * aggregates over (a `signedSplit` GL amount, for instance, sums the distribution lines).
   *
   * On an `arraySummary` this is the collection KEY — the key the row carries the array under, and
   * the key its `arrayField` columns declare. It defaults to the summary's own `field` when absent,
   * which is how every summary in the Phase 1 config is spelled; the projection resolves that default
   * once and uses the result for the row read, the column match and the aggregate alike, so naming a
   * collection differently from the field that summarises it is supported rather than silently
   * degrading to an item count.
   */
  collection?: string
  listType?: ListType
  /**
   * The field's own allowed values, as the document serves them.
   *
   * Read STRAIGHT from here — there is no id to dereference and no value service hop. The earlier
   * design named a list in a checked-in fixture by id, and the document has never carried that
   * property on any of its 258 fields: every lookup resolved `null`, so all 44 `listType: "enum"`
   * fields rendered as free text while 38 of them had their options sitting in the document unread.
   * Nothing threw; the dropdowns were simply absent (PVAL-26493).
   *
   * Absent => free-text entry, which is still the honest answer for the 47 fields that declare a
   * list type and carry no values.
   */
  listValues?: TransactionListValues
  comparison?: ComparisonMapping
  /** Labels for the two halves of a `signedSplit` field. */
  split?: { debitLabel: string; creditLabel: string }
  /**
   * Whether this field is filled by a derivation, and what its `{tokens}` read.
   *
   * Authored on the field that RECEIVES the value, never on the one that triggers it — the same
   * grain as every other property, so one field's metadata stays its whole story. Fan-out then
   * needs no list-valued column: Trade Currency and Settle Currency each name the instrument
   * endpoint themselves, and both fill from the one answer.
   */
  derivedFrom?: DerivedFrom
  /**
   * What the derivation reads, as served (PVAL-26685). The URL, the reply path and the token names
   * stay on RPS; the dialog sends these inputs' values to `/resolve` as one `Derivation` item and
   * writes back the scalar it answers.
   */
  derivationInputs?: DerivationInput[]
  /**
   * The property of the upstream reply that holds the derived value, as served (PVAL-26685) — a
   * dotted name, like a list's `displayText`. RPS is meant to answer the scalar itself
   * (PVAL-26675); until it does the reply is the raw upstream body, and the dialog reads the value
   * out of it by this name.
   */
  derivationDisplayText?: string
  settings: FieldSettings
}

/** A curated dialog context: which key groups compose it, and what the modal is titled. */
export interface TranCategoryDefinition {
  key: TranCategoryKey
  /**
   * The schema's own name — "Listed", "Interest Rate Swap", "GL Entry".
   *
   * This is the only name a tran category carries. The dialog's heading names the action ("New
   * Transaction" / "Edit Transaction") and is not per-tran-category, so there is deliberately no
   * `title`/`createTitle` here: the schema is named once, in the header's breadcrumb.
   */
  name: string
  /**
   * Which Create-menu submenu this tran category sits under, and the submenu's label.
   *
   * Absent means top level with no submenu — `glEntry` is the one such tran category today, and it is
   * absent rather than empty-string so "no group" cannot be confused with a group named `''`.
   *
   * The Create menu is built from this and nothing else (see
   * {@link TransactionMetadataSource.createMenu}): the submenu's label IS this value, so adding a
   * second group, renaming "Trade", or moving a schema between groups is a metadata edit with no UI
   * change. Do not reintroduce a hardcoded menu beside it.
   */
  menuGroup?: string
  keyGroups: KeyGroup[]
  loadoutCondition?: LoadoutCondition
  /**
   * Deliberately no `collections`.
   *
   * A tran category used to name the collections it rendered, which made a collection two facts that had
   * to agree — and they drifted: `otherPayments` and `keywords` are in `common` so every trade
   * dialog renders their summary rows, but only `listedTrade` named them, and `nearLeg`/`farLeg`
   * were named by nothing. The projection now derives collections from the `arraySummary` fields
   * the tran category actually composes, so a summary row and its items modal come from one field and
   * cannot disagree.
   */
}

/**
 * One entry in the Create menu.
 *
 * Built entirely from {@link TranCategoryDefinition} by
 * {@link TransactionMetadataSource.createMenu} — the label is the tran category's `name`, the
 * nesting is its `menuGroup`, and what the item opens is its `key`. Nothing about the menu is
 * authored in the UI.
 *
 * It lives in the model rather than beside the grid that renders it because the shape is metadata,
 * not presentation: the component that draws it and the service that derives it must agree on ONE
 * definition, and a second structurally-compatible copy beside the component is exactly the kind of
 * mapping this design is trying not to have.
 */
export interface CreateMenuItem {
  label: string
  /**
   * The tran category the item opens. Absent on the Clearwater posting types (out of scope for
   * PVAL-26341) and on a submenu header, which is a hover target only.
   */
  tranCategory?: TranCategoryKey
  /**
   * Nested items shown as a hover-opened side flyout. An item with children is a hover target only;
   * it never itself carries a `tranCategory`.
   */
  children?: readonly CreateMenuItem[]
}

/** One dialog context per Enfusion schema: the fourteen trade types, plus GL Entry. */
export type TranCategoryKey = string

/** The whole checked-in metadata document. Shaped like a response so an endpoint can replace it. */
export interface TransactionMetadataConfig {
  /** Bumped whenever the checked-in config changes, so a cache can be invalidated on it. */
  version: string
  tranCategories: TranCategoryDefinition[]
  fields: TransactionFieldMetadata[]
}
